import useDocumentTitle from '@/components/Helmet';
import { useCustomLoader } from '@/components/Loader';
import CustomTable from '@/components/material/CustomTable';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useToast } from '@/hooks/use-toast';
import axiosInstance from '@/lib/axiosInstance';
import { Box, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Grid, TextField, Typography } from '@mui/material'
import { Edit, Eye, Pencil, Plus, Trash2 } from 'lucide-react';
import React, { useCallback, useEffect, useState } from 'react'
import { useLocation } from 'wouter'
import { getVendorsData } from '@/apis/apis';

const Index = () => {
    useDocumentTitle("Vendors");
    const loader = useCustomLoader();
    const { toast } = useToast();
    const [_, navigate] = useLocation();
    const [vendors, setVendors] = useState([]);
    const [counts, setCounts] = useState(0);
    const [page, setPage] = useState(0);
    const [limit, setLimit] = useState(10);
    const [search, setSearch] = useState("");
    const [deleteModal, setDeleteModal] = useState<any>(null);

    const fetchVendors = useCallback(async (search : string) => {
        loader(true);
        try {
            const response = await getVendorsData({ page, limit, search });

            const vendors = response.vendors.map((vendor: any) => ({
                ...vendor,
                active: vendor.active ? "Active" : "Inactive",
            }));

            setVendors(vendors);
            setCounts(response.counts);
        } catch (error) {
            toast({
                variant: "destructive",
                title: "Error!",
                description: "Something went wrong",
            });
        } finally {
            loader(false);
        }
    }, [page, limit]);

    useEffect(() => {
        const timer = setTimeout(() => {
            fetchVendors(search);
        }, search ? 600 : 0);

        return () => clearTimeout(timer);
    }, [fetchVendors,search]);

    const updateStatus = async (row: any) => {
        try {
            const { data: response } = await axiosInstance.put(`/api/vendors/${row._id}`, {
                active: row.active === "Active" ? false : true
            });
            if (response.success) {
                toast({
                    variant: "success",
                    title: "Success!",
                    description: "Status updated successfully",
                })
                fetchVendors(search);
            }
            else {
                toast({
                    variant: "destructive",
                    title: "Error!",
                    description: response.message,
                })
            }
        } catch (error) {
            toast({
                variant: "destructive",
                title: "Error!",
                description: "Something went wrong",
            })
        }
    }

    const deleteVendor = async (id: string) => {
        try {
            await axiosInstance.delete(`/api/vendors/${id}`);
            fetchVendors(search);
            setDeleteModal(null);
            toast({
                variant: "success",
                title: "Success!",
                description: "Vendor deleted successfully",
            })
        } catch (error) {
            toast({
                variant: "destructive",
                title: "Error!",
                description: "Something went wrong",
            })
        }
    }

    return (
        <div>
            <Box className='flex justify-between mb-2'>
                <Typography variant='h6' >Vendors</Typography>
                <Button onClick={() => navigate('/vendors/add')}><Plus /> Add</Button>
            </Box>
            <Box >
                <Grid container spacing={2}>
                    <Grid size={{ md: 4, sm: 6, xs: 12 }}>
                        <TextField size='small' fullWidth placeholder='Search Vendor by name or email' value={search} onChange={(e) => setSearch(e.target.value)} />
                    </Grid>

                </Grid>
            </Box>
            <CustomTable
                headers={[
                    { id: "name", label: "Name" },
                    { id: "email", label: "Email" },
                    { id: "phone", label: "Phone" },
                    { id: "specialties", label: "Specialties" },
                    { id: "rating", label: "Rating" },
                    { id: "active", label: "Active" },
                ]}
                data={vendors}
                count={counts}
                page={page}
                limit={limit}
                setPage={setPage}
                setLimit={setLimit}
                updateStatus={updateStatus}
                actions={[
                    // { label: "View", icon: <Eye size={16} />, onClick: (row) => console.log("View", row) },
                    { label: "Edit", icon: <Pencil size={16} />, onClick: (row) => navigate(`/vendors/edit/${row._id}`), color: "black" },
                    { label: "Delete", icon: <Trash2 size={16} />, onClick: (row) => setDeleteModal(row), color: "red" },
                ]}
            />
            <Dialog open={deleteModal !== null} onClose={() => setDeleteModal(null)}>
                <DialogTitle>Delete Vendor</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        <Typography>Are you sure you want to delete this <span className='font-semibold text-red-500'>{deleteModal?.name}</span> vendor?</Typography>
                    </DialogContentText>
                    <DialogActions sx={{ width: "100%" }}>
                        <Box className='w-full flex justify-between'>
                            <Button onClick={() => setDeleteModal(null)}>Cancel</Button>
                            <Button onClick={() => deleteVendor(deleteModal?._id)} variant="error">Yes, Delete It</Button>
                        </Box>
                    </DialogActions>
                </DialogContent>
            </Dialog>
        </div>
    )
}

export default Index
